import { useState } from 'react';

interface Integration {
  name: string;
  icon: string;
  category: string;
  description: string;
}

const categories = ['All', 'SMS', 'Email', 'Messaging', 'Payments', 'OMS', 'POS', 'Delivery', 'IoT'];

const integrations: Integration[] = [
  // SMS
  { name: 'Twilio', icon: 'ri-message-2-line', category: 'SMS', description: 'SMS delivery updates and notifications' },
  { name: 'Infobip', icon: 'ri-chat-smile-2-line', category: 'SMS', description: 'Global SMS and omnichannel messaging' },
  { name: 'Gupshup', icon: 'ri-message-3-line', category: 'SMS', description: 'SMS and conversational messaging' },
  { name: 'Textlocal', icon: 'ri-smartphone-line', category: 'SMS', description: 'SMS for Indian market' },
  { name: 'Airtel', icon: 'ri-send-plane-line', category: 'SMS', description: 'Airtel SMS and business APIs' },
  // Email
  { name: 'SendGrid', icon: 'ri-mail-send-line', category: 'Email', description: 'Transactional email and delivery notifications' },
  { name: 'Amazon SES', icon: 'ri-mail-line', category: 'Email', description: 'Scalable email sending and tracking' },
  // Messaging
  { name: 'WhatsApp', icon: 'ri-whatsapp-line', category: 'Messaging', description: 'WhatsApp Business notifications and tracking' },
  { name: 'Line', icon: 'ri-chat-1-line', category: 'Messaging', description: 'Line messaging for customer updates' },
  { name: 'Slack', icon: 'ri-slack-line', category: 'Messaging', description: 'Team alerts and delivery updates' },
  // Payments
  { name: 'Razorpay', icon: 'ri-bank-card-line', category: 'Payments', description: 'Payments and COD reconciliation (India)' },
  { name: 'Stripe', icon: 'ri-bank-card-2-line', category: 'Payments', description: 'Online payment processing' },
  { name: 'PayPal', icon: 'ri-paypal-line', category: 'Payments', description: 'PayPal payment collection' },
  // OMS / E-commerce
  { name: 'Unicommerce', icon: 'ri-box-3-line', category: 'OMS', description: 'Order management and fulfillment' },
  { name: 'Shopify', icon: 'ri-shopping-bag-line', category: 'OMS', description: 'Auto-import Shopify orders' },
  { name: 'Big Commerce', icon: 'ri-store-2-line', category: 'OMS', description: 'BigCommerce delivery automation' },
  { name: 'WooCommerce', icon: 'ri-store-line', category: 'OMS', description: 'WordPress delivery management' },
  { name: 'Wix', icon: 'ri-window-line', category: 'OMS', description: 'Wix e-commerce delivery' },
  // POS
  { name: 'Toast', icon: 'ri-restaurant-line', category: 'POS', description: 'Sync orders from Toast POS' },
  { name: 'Square', icon: 'ri-layout-grid-line', category: 'POS', description: 'Connect Square POS for orders' },
  { name: 'Lightspeed', icon: 'ri-flashlight-line', category: 'POS', description: 'Lightspeed restaurant POS' },
  // Delivery
  { name: 'DoorDash', icon: 'ri-door-open-line', category: 'Delivery', description: 'DoorDash Drive integration' },
  { name: 'Uber Direct', icon: 'ri-taxi-line', category: 'Delivery', description: 'On-demand Uber drivers' },
  { name: 'GrubHub', icon: 'ri-takeaway-line', category: 'Delivery', description: 'GrubHub order management' },
  { name: 'Google Maps', icon: 'ri-map-line', category: 'Delivery', description: 'Maps, routing, and ETAs' },
  // IoT
  { name: 'IoT devices', icon: 'ri-sensor-line', category: 'IoT', description: 'Sensors, trackers, and fleet telematics' },
];

import SEO, { breadcrumbJsonLd } from '../../components/SEO';
import { API_DOCS_URL, SIGNUP_URL } from '../../data/links';

export default function IntegrationsPage() {
  const [activeCategory, setActiveCategory] = useState('All');

  const filtered = activeCategory === 'All'
    ? integrations
    : integrations.filter((i) => i.category === activeCategory);

  return (
    <div className="pt-24">
      <SEO
        title="Integrations"
        description="Connect your tools and optimize deliveries with 50+ integrations: SMS, email, payment gateways, IoT devices, OMS platforms like Shopify and Unicommerce, and more."
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', url: 'https://geofleet.ai' },
          { name: 'Integrations', url: 'https://geofleet.ai/integrations' },
        ])}
      />
      {/* Hero */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center space-x-2 bg-[#eef5ff] text-[#1769ff] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <i className="ri-plug-line"></i>
            <span>Integrations</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Connect your tools, optimize your deliveries
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Connect your tools and optimize deliveries: 10+ SMS integrations, 10+ email providers, 10+ payment gateways, 5,000+ IoT devices, and 10+ OMS like Shopify and Unicommerce.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-center">
            {[
              { value: '50+', label: 'Integrations' },
              { value: '5 min', label: 'Setup time' },
              { value: '99.99%', label: 'Uptime' },
            ].map((stat, i) => (
              <div key={i} className="px-6">
                <div className="text-2xl font-bold text-[#1769ff]">{stat.value}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Filter + Grid */}
      <section className="py-16 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6">
          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer ${
                  activeCategory === cat
                    ? 'bg-[#1769ff] text-white shadow-lg shadow-[#1769ff]/20'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Integration Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {filtered.map((integration, index) => (
              <div
                key={index}
                className="bg-white rounded-xl p-6 border border-gray-100 hover:shadow-lg hover:border-[#1769ff]/30 transition-all group cursor-pointer"
              >
                <div className="w-12 h-12 bg-[#eef5ff] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#1769ff] transition-colors">
                  <i className={`${integration.icon} text-xl text-[#1769ff] group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-1">{integration.name}</h3>
                <p className="text-sm text-gray-500">{integration.description}</p>
                <div className="mt-3">
                  <span className="text-xs font-medium text-[#1769ff] bg-[#eef5ff] px-2.5 py-1 rounded-full">
                    {integration.category}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* API Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Build custom integrations with our API
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Use our REST API and webhooks to build custom integrations tailored to your workflow. The API reference lives inside the Geofleet app and opens once you sign in.
              </p>
              <div className="space-y-4 mb-8">
                {['RESTful API with full CRUD operations', 'Real-time webhooks for order events', 'MCP server for AI assistants and agents', 'Sandbox environment for testing'].map((item, i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <i className="ri-check-line text-[#1769ff]"></i>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <a href={API_DOCS_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-1.5 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all shadow-md shadow-[#1769ff]/15 cursor-pointer hover:opacity-90"
              style={{ backgroundColor: '#1769ff', color: 'white' }}>
                <span>View API Docs</span>
                <i className="ri-arrow-right-line text-sm"></i>
              </a>
            </div>
            <div className="rounded-2xl bg-gray-900 p-8 text-white">
              <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl bg-white/10">
                <i className="ri-code-s-slash-line text-2xl text-blue-300" aria-hidden />
              </div>
              <h3 className="text-xl font-bold">Get API access in three steps</h3>
              <ol className="mt-6 space-y-4">
                {[
                  { title: 'Create an account', body: 'Sign up at app.geofleet.ai.' },
                  { title: 'Open the API reference', body: 'Go to Support → API docs inside the Geofleet app.' },
                  { title: 'Connect your system', body: 'Push orders, pull status and subscribe to webhooks from your OMS, ERP or store.' },
                ].map((step, i) => (
                  <li key={step.title} className="flex gap-4">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-sm font-bold">{i + 1}</span>
                    <div>
                      <p className="font-semibold">{step.title}</p>
                      <p className="mt-1 text-sm leading-relaxed text-gray-400">{step.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
              <a
                href={SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center gap-1.5 rounded-lg bg-white px-5 py-2.5 text-sm font-semibold text-gray-900 hover:bg-gray-100"
              >
                Create an account
                <i className="ri-arrow-right-line text-sm" aria-hidden />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1769ff]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Don't see your integration?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            We're constantly adding new integrations. Let us know what you need and we'll make it happen.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://meetings.hubspot.com/aditya145" target="_blank" rel="noopener noreferrer" className="bg-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-all shadow-md cursor-pointer"
            style={{ color: '#1769ff' }}>
              Request an Integration
            </a>
            <a href="https://app.geofleet.ai/signup" target="_blank" rel="noopener noreferrer" className="border-2 border-white/80 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-white/10 transition-all cursor-pointer">
              Build with our API
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
